import { Form, Spin, Card, Divider } from 'antd';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom, isChildSchoolAge } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import Select from 'components/Select/Select';
import RadioCards from 'components/RadioCards/RadioCards';

const TIME_OPTIONS = (() => {
  const options = [];
  for (let hour = 0; hour <= 23; hour++) {
    for (const minute of [0, 30]) {
      const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${h}:${String(minute).padStart(2, '0')} ${ampm}`;
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      options.push({ label, value });
    }
  }
  return options;
})();

const TIME_SELECT_PROPS = {
  options: TIME_OPTIONS,
  placeholder: 'Select time',
  getOptionValue: (v) => v.value,
  getOptionLabel: (v) => v.label,
  isOptionSelected: (option, values) =>
    values.some((val) => (typeof val === 'string' ? val : val?.value) === option.value),
  onChangeFormatter: (v) => v?.value,
  isClearable: false,
  isSearchable: false,
};

const SAME_EVERY_DAY_OPTIONS = [
  { value: 'YES', label: 'Yes, same every day', description: 'Most families' },
  { value: 'NO', label: 'No, some days are different', description: 'e.g. early dismissal, different hours' },
];

export function FamilyApplicationChildrenDailyTimeBlocks({
  application,
  setApplication,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });
  const existingSchedule = currentChild?.schedules?.[0];

  const careNeedType = existingSchedule?.careNeedType?.code ?? null;

  const showMorning = ['BEFORE_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL', 'SUMMER', 'NON_SCHOOL'].includes(careNeedType);
  const showAfternoon = ['AFTER_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL'].includes(careNeedType);

  const CARE_TYPE_LABELS = {
    BEFORE_SCHOOL: 'before school',
    AFTER_SCHOOL: 'after school',
    BEFORE_AND_AFTER_SCHOOL: 'before & after school',
    SUMMER: 'summer',
  };
  const isSchoolAgeInSchool = isChildSchoolAge(currentChild) && currentChild?.inSchool === true;
  const careTypeLabel = isSchoolAgeInSchool ? CARE_TYPE_LABELS[careNeedType] : null;
  const bannerQuestion = careTypeLabel
    ? `${childFirstName}'s ${careTypeLabel} schedule`
    : `${childFirstName}'s care schedule`;

  const sameEveryDay = Form.useWatch('sameEveryDay', form);
  const _differentDays = Form.useWatch('differentDays', form);
  const watchedDifferentDays = useMemo(() => _differentDays || [], [_differentDays]);

  const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayOptions = (existingSchedule?.days || [])
    .slice()
    .sort((a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek))
    .map((d) => ({ value: d.dayOfWeek, label: d.dayOfWeek }));
  const showSameEveryDay = (existingSchedule?.days || []).length > 1;

  const renderMorningFields = (prefix) => {
    const n = (field) => (prefix.length ? [...prefix, field] : field);
    return (
      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name={n('morningDropOff')}
          label="Drop-off"
          rules={[{ required: true, message: 'Required' }]}
          className="mb-0"
        >
          <Select {...TIME_SELECT_PROPS} />
        </Form.Item>
        <Form.Item
          name={n('morningLeaveForSchool')}
          label={['SUMMER', 'NON_SCHOOL'].includes(careNeedType) ? 'Pick-up' : 'Goes to school'}
          dependencies={[n('morningDropOff')]}
          rules={[
            { required: true, message: 'Required' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const dropOff = getFieldValue(n('morningDropOff'));
                if (!value || !dropOff || value > dropOff) return Promise.resolve();
                return Promise.reject(new Error('Must be after drop-off time'));
              },
            }),
          ]}
          className="mb-0"
        >
          <Select {...TIME_SELECT_PROPS} />
        </Form.Item>
      </div>
    );
  };

  const renderAfternoonFields = (prefix) => {
    const n = (field) => (prefix.length ? [...prefix, field] : field);
    return (
      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name={n('afternoonArriveFromSchool')}
          label="Arrives from school"
          dependencies={[n('morningLeaveForSchool')]}
          rules={[
            { required: true, message: 'Required' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const morningEnd = getFieldValue(n('morningLeaveForSchool'));
                if (!value || !morningEnd || value > morningEnd) return Promise.resolve();
                return Promise.reject(new Error('Must be after the morning "Goes to school" time'));
              },
            }),
          ]}
          className="mb-0"
        >
          <Select {...TIME_SELECT_PROPS} />
        </Form.Item>
        <Form.Item
          name={n('afternoonPickUp')}
          label="Pick-up"
          dependencies={[n('afternoonArriveFromSchool')]}
          rules={[
            { required: true, message: 'Required' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const arriveFromSchool = getFieldValue(n('afternoonArriveFromSchool'));
                if (!value || !arriveFromSchool || value > arriveFromSchool) return Promise.resolve();
                return Promise.reject(new Error('Must be after arrives from school time'));
              },
            }),
          ]}
          className="mb-0"
        >
          <Select {...TIME_SELECT_PROPS} />
        </Form.Item>
      </div>
    );
  };

  // Pre-populate from existing schedule days (times are uniform — read from first day)
  useEffect(() => {
    const days = existingSchedule?.days;
    if (!days?.length) return;

    const firstDay = days[0];
    const morningBlock = firstDay.morning ?? firstDay.summer ?? firstDay.nonSchool ?? null;
    const trimTime = (t) => t?.slice(0, 5) || undefined;

    form.setFieldsValue({
      morningDropOff: trimTime(morningBlock?.startTime),
      morningLeaveForSchool: trimTime(morningBlock?.endTime),
      afternoonArriveFromSchool: trimTime(firstDay.afternoon?.startTime),
      afternoonPickUp: trimTime(firstDay.afternoon?.endTime),
    });

    if (days.length < 2) return;

    // Detect per-day overrides by comparing each day against the first day
    const refMorningStart = trimTime(morningBlock?.startTime);
    const refMorningEnd = trimTime(morningBlock?.endTime);
    const refAfternoonStart = trimTime(firstDay.afternoon?.startTime);
    const refAfternoonEnd = trimTime(firstDay.afternoon?.endTime);

    const differentDaysList = days
      .filter((day, i) => {
        if (i === 0) return false;
        const dm = day.morning ?? day.summer ?? day.nonSchool ?? null;
        return (
          trimTime(dm?.startTime) !== refMorningStart ||
          trimTime(dm?.endTime) !== refMorningEnd ||
          trimTime(day.afternoon?.startTime) !== refAfternoonStart ||
          trimTime(day.afternoon?.endTime) !== refAfternoonEnd
        );
      })
      .map((d) => d.dayOfWeek);

    if (differentDaysList.length > 0) {
      const dayOverrides = {};
      differentDaysList.forEach((dayOfWeek) => {
        const dayData = days.find((d) => d.dayOfWeek === dayOfWeek);
        if (!dayData) return;
        const dm = dayData.morning ?? dayData.summer ?? null;
        dayOverrides[dayOfWeek] = {
          morningDropOff: trimTime(dm?.startTime),
          morningLeaveForSchool: trimTime(dm?.endTime),
          afternoonArriveFromSchool: trimTime(dayData.afternoon?.startTime),
          afternoonPickUp: trimTime(dayData.afternoon?.endTime),
        };
      });
      form.setFieldsValue({ sameEveryDay: 'NO', differentDays: differentDaysList, dayOverrides });
    } else {
      form.setFieldsValue({ sameEveryDay: 'YES' });
    }
  }, [existingSchedule, form]);

  // Pre-fill per-day overrides with uniform times when a day is first added
  useEffect(() => {
    if (!watchedDifferentDays.length) return;

    const uniform = form.getFieldsValue([
      'morningDropOff',
      'morningLeaveForSchool',
      'afternoonArriveFromSchool',
      'afternoonPickUp',
    ]);

    const updates = {};
    watchedDifferentDays.forEach((dayOfWeek) => {
      const existing = form.getFieldValue(['dayOverrides', dayOfWeek]);
      const hasValues =
        existing?.morningDropOff ||
        existing?.morningLeaveForSchool ||
        existing?.afternoonArriveFromSchool ||
        existing?.afternoonPickUp;
      if (!hasValues) {
        updates[dayOfWeek] = { ...uniform };
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue({ dayOverrides: updates });
    }
  }, [watchedDifferentDays, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({ applicationId: _newApplication.id, newStep: nextStep });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const days = (existingSchedule?.days || []).map((day) => {
          const isDifferentDay = values.sameEveryDay === 'NO' && (values.differentDays || []).includes(day.dayOfWeek);
          const dayVals = isDifferentDay ? values.dayOverrides?.[day.dayOfWeek] ?? values : values;
          const timeBlock = { startTime: dayVals.morningDropOff, endTime: dayVals.morningLeaveForSchool };
          return {
            ...day,
            morning: ['BEFORE_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL'].includes(careNeedType) ? timeBlock : null,
            afternoon: showAfternoon
              ? { startTime: dayVals.afternoonArriveFromSchool, endTime: dayVals.afternoonPickUp }
              : null,
            summer: careNeedType === 'SUMMER' ? timeBlock : null,
            nonSchool: careNeedType === 'NON_SCHOOL' ? timeBlock : null,
          };
        });

        const savedSchedule = await familyService.saveChildSchedule(
          application.id,
          currentChild.id,
          existingSchedule?.id || null,
          { ...existingSchedule, days },
        );

        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = {
          ...currentChild,
          schedules: [savedSchedule, ...(currentChild.schedules || []).filter((s) => s.id !== savedSchedule.id)],
        };

        const updatedApplication = { ...application, children: updatedChildren };
        const finalApplication = await familyService.saveApplication(updatedApplication);
        setApplication(finalApplication);

        _goToNextStep(finalApplication);
      } catch (error) {
        console.error('Error saving daily time blocks:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      application,
      careNeedType,
      currentChild,
      currentChildIndex,
      existingSchedule,
      readOnly,
      setApplication,
      setStep,
      showAfternoon,
      stepConfig,
    ],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Daily Time Blocks`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner
              question={bannerQuestion}
              guidance="Your best estimate is fine — you can always update this later"
              className="my-12"
            />

            {showMorning && (
              <Card
                title={
                  careNeedType === 'SUMMER'
                    ? 'Daily care window'
                    : careNeedType === 'NON_SCHOOL'
                    ? 'Daily care'
                    : 'Morning care (before school)'
                }
                className="rounded-xl"
              >
                {renderMorningFields([])}
              </Card>
            )}

            {showAfternoon && (
              <Card title="Afternoon care (after school)" className="rounded-xl">
                {renderAfternoonFields([])}
              </Card>
            )}
          </div>
        </div>

        {showSameEveryDay && (
          <>
            <Divider className="!my-16" />

            <div className="p-7 pt-0">
              <div className="space-y-6">
                <Banner
                  question="Is this schedule the same every day?"
                  guidance="Let us know if any days have different times."
                  className="mb-12"
                />

                <Form.Item name="sameEveryDay" rules={[{ required: true, message: 'Please select an option' }]}>
                  <RadioCards options={SAME_EVERY_DAY_OPTIONS} />
                </Form.Item>

                {sameEveryDay === 'NO' && (
                  <>
                    <div className="!my-12">
                      <Form.Item
                        name="differentDays"
                        rules={[
                          {
                            required: true,
                            message: null,
                          },
                          {
                            validator: (_, v) =>
                              Array.isArray(v) && v.length > 0
                                ? Promise.resolve()
                                : Promise.reject(new Error('Select at least one day')),
                          },
                        ]}
                        label="Which days are different?"
                      >
                        <RadioCards options={dayOptions} multiple direction="horizontal" />
                      </Form.Item>
                    </div>

                    {[...watchedDifferentDays]
                      .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
                      .map((dayOfWeek) => (
                        <Card key={dayOfWeek} title={dayOfWeek} className="rounded-xl">
                          <div className="space-y-4">
                            {showMorning && (
                              <div>
                                <p className="text-sm text-gray-500 mb-3">
                                  {careNeedType === 'SUMMER'
                                    ? 'Daily care window'
                                    : careNeedType === 'NON_SCHOOL'
                                    ? 'Daily care'
                                    : 'Morning care (before school)'}
                                </p>
                                {renderMorningFields(['dayOverrides', dayOfWeek])}
                              </div>
                            )}
                            {showAfternoon && (
                              <div>
                                <p className="text-sm text-gray-500 mb-3">Afternoon care (after school)</p>
                                {renderAfternoonFields(['dayOverrides', dayOfWeek])}
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({ applicationId: application?.id, newStep: previousStep });
            },
            disabled: submitting,
            'data-testid': 'children-daily-time-blocks-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-daily-time-blocks-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
